<template>
	<div class="h-full">
		<client-only>
			<fetch-loader v-if="mapDataError || participationPolylineError || mapDataPending || participationPolylinePending"
						  :error="mapDataError || participationPolylineError"
						  :pending="participationPolylinePending || mapDataPending" @retry="refreshAll" fill/>
			<event-map v-else :event="props.event" :tracks="mapData.tracks"
					   :additional-zoom-elements="mapFocusElements"
					   :zoom="8"
					   hide-event-marker downloadable class="stretch-layout" @dragged="hasEnabledAutoFocus = false">

				<template v-if="props.participation.route && props.downloadable" #action_controls>
					<btn small icon class="w-36 min-w-36 h-36 min-h-36" @click="downloadTrack" :title="$t('action.download')">
						<Icon :icon="icons.download"/>
					</btn>
				</template>

				<template #controls>
					<btn-group v-if="!hasEnabledAutoFocus" vertical>
						<btn small icon class="w-36 min-w-36 h-36 min-h-36" @click="focusParticipant" :title="$t('action.show_on_map')">
							<Icon :icon="icons.autofocus"/>
						</btn>
					</btn-group>
				</template>

				<template v-for="trackSpot in mapData.trackSpots">
					<map-marker v-if="trackSpot.type === TrackSpotType.Result && trackSpot.lat && trackSpot.lng"
								:height="28" :width="28"
								:color="trackSpot._tracked ? trackSpot.color : '#7D8484'"
								:icon="getIconForTrackSpot(trackSpot)"
								:lat-lng="[trackSpot.lat, trackSpot.lng]" @click="handleTrackSpotClick(trackSpot)"/>
					<map-marker v-else-if="trackSpot.type === TrackSpotType.Control && trackSpot.lat && trackSpot.lng"
								:height="28" :width="28"
								:color="trackSpot._tracked ? trackSpot.color : '#7D8484'"
								:icon="icons.control_track_spot"
								:lat-lng="[trackSpot.lat, trackSpot.lng]" @click="handleTrackSpotClick(trackSpot)"/>
					<map-marker v-else-if="trackSpot.type === TrackSpotType.Navigation && trackSpot.lat && trackSpot.lng"
								:height="20" :width="20"
								:color="trackSpot._tracked ? trackSpot.color : '#7D8484'"
								:icon="icons.track_spot_navigation"
								:lat-lng="[trackSpot.lat, trackSpot.lng]" @click="handleTrackSpotClick(trackSpot)"/>
				</template>

				<template v-if="safeParticipationPolyline?.length">
					<map-polyline v-if="preSafeParticipationPolyline && !props.hideSafeBounding" :lat-lngs="preSafeParticipationPolyline" distance-info dashed color="#7D8484"/>
					<map-polyline :lat-lngs="safeParticipationPolyline" distance-info dashed color="#f0820a"/>
					<map-polyline v-if="postSafeParticipationPolyline && !props.hideSafeBounding" :lat-lngs="postSafeParticipationPolyline" distance-info dashed color="#7D8484"/>

					<template v-if="!hasBoundingResultTrackSpots && !props.hideSafeBounding">
						<map-marker :height="28" :width="28"
									:color="props.highlightSafeBounding ? '#7D8484' : (props.event.color ?? $config.public.colors.primary)"
									:icon="icons.start"
									:lat-lng="[participationPolyline[0].lat, participationPolyline[0].lng]"/>

						<map-marker v-if="hasFinished" :height="28" :width="28"
									:color="props.highlightSafeBounding ? '#7D8484' : (props.event.color ?? $config.public.colors.primary)"
									:icon="icons.finish"
									:lat-lng="[participationPolyline[participationPolyline.length - 1].lat, participationPolyline[participationPolyline.length - 1].lng]"/>
					</template>

					<map-marker v-if="!props.hideSafeBounding || (selectedParticipationPolylineIndex > safeParticipationPolylineStartIndex && selectedParticipationPolylineIndex < safeParticipationPolylineEndIndex)" :lat-lng="participationPolyline[selectedParticipationPolylineIndex]"
								:height="24" :width="24"
								no-arrow
								pulse
								:image="$image.format(props.participation.avatar_thumbnail, {width: (24 * 2), height: (24 * 2)})"
								:color="props.event.color ?? $config.public.colors.primary"/>
				</template>

				<template v-if="participationPolyline.length || $slots.footer" #footer>
					<div class="bg-white">
						<slot name="footer"/>
						<polyline-chart v-if="participationPolyline.length" v-model="selectedParticipationPolylineIndex" :polyline="participationPolyline" :pace-type="mapData.pace_type" :height="200"/>
					</div>
				</template>

			</event-map>
		</client-only>

		<slide-sheet v-if="selectedTrackSpot" v-model="isTrackSpotDetailsModalVisible" :width="420">
			<template #default>
				<track-spot-popup-card :track-spot="selectedTrackSpot">
					<divider margin="16"/>

					<alert v-if="!selectedTrackSpot.records || !selectedTrackSpot.records.length" :content="$t('missing.results.index')" :icon="false" no-spacing
						   small/>
					<template v-else>
						<description-list v-for="record in selectedTrackSpot.records" :key="`record-${record.id}`" small>
							<template v-if="record.track_spot_position > 1">
								<description-list-item v-if="selectedTrackSpot.records.length > 1" title="#" :text="record.track_spot_position"/>
								<description-list-item
									v-if="record.tracking_time"
									:title="$t('enum.rank_metric.time.title')" :text="record.tracking_time"/>
							</template>
							<description-list-item v-else :title="$t('label.start_station')" :text="$date(record.tracked_at).format('LLL')"/>
						</description-list>
					</template>
				</track-spot-popup-card>
			</template>
		</slide-sheet>
	</div>
</template>

<script setup lang="ts">
import {ParticipationStatus, TrackSpotSubtype, TrackSpotType} from "@spoferan/spoferan-ts-core";
import type {EventPreview, Model} from "@spoferan/spoferan-ts-core";
import {useApiFetch, useNuxtApp} from '#imports';
import {ref, computed, onMounted, onBeforeUnmount, watch} from 'vue';
import {
	mdiClockStart,
	mdiCrosshairsGps,
	mdiFlagCheckered,
	mdiMotionPauseOutline,
	mdiMotionPlayOutline,
	mdiNavigationOutline,
	mdiTimelineCheckOutline,
	mdiTimerOutline,
	mdiUpdate
} from "@mdi/js";
import {downloadIcon} from "../../config";

const props = defineProps<{
	event: EventPreview,
	participation: Model,

	// Whether hidden track spots should be shown
	withHidden?: boolean,

	// Whether the participation track can be downloaded
	downloadable?: boolean,

	// Whether 400m safe distance from start and finish shall be hidden
	hideSafeBounding?: boolean,

	// Whether 400m safe distance from start and finish shall be marked as invisible to public
	highlightSafeBounding?: boolean,

	lazy?: boolean
}>();

const {$date, $apiFetch, $analytics, $downloadFile, $echo} = useNuxtApp()

const icons = {
	start: mdiClockStart,
	finish: mdiFlagCheckered,
	round_result_tracking_spot: mdiUpdate,
	control_track_spot: mdiTimelineCheckOutline,
	track_spot_navigation: mdiNavigationOutline,
	download: downloadIcon,
	autofocus: mdiCrosshairsGps
};


const {reactiveData: mapData, error: mapDataError, pending: mapDataPending, refresh: refreshMapData} = await useApiFetch(`/v1/view/events/${props.event.id}/participations/${props.participation.id}/tracking`, {
	guest: true,
	lazy: props.lazy,
	params: {
		ts: props.participation.cache_buster,
		with_hidden: props.withHidden
	}
});

const {reactiveData: participationPolyline, error: participationPolylineError, pending: participationPolylinePending, refresh: refreshPolyline} = await useApiFetch(`/v1/events/${props.event.id}/participations/${props.participation.id}/track`, {
	guest: true,
	lazy: props.lazy,
	params: {
		ts: props.participation.cache_buster
	}
});

const selectedTrackSpot = ref(null);
const isTrackSpotDetailsModalVisible = ref(false);

const safeBoundingDistanceInMeters = 400;
const safeParticipationPolylineStartIndex = computed(() => {
	if (!participationPolyline.value || (!props.hideSafeBounding && !props.highlightSafeBounding)) {
		return 0;
	}

	return participationPolyline.value.findIndex(trackSpot => trackSpot.distance_from_start >= safeBoundingDistanceInMeters);
});
const safeParticipationPolylineEndIndex = computed(() => {
	if (!participationPolyline.value?.length) {
		return 0;
	}
	if (!props.hideSafeBounding && !props.highlightSafeBounding) {
		return participationPolyline.value.length - 1;
	}

	const finalDistance = participationPolyline.value[participationPolyline.value.length - 1].distance_from_start;
	const safeFinishDistance = finalDistance - safeBoundingDistanceInMeters;

	return Math.max(participationPolyline.value.findIndex(trackSpot => trackSpot.distance_from_start >= safeFinishDistance, safeParticipationPolylineStartIndex.value));
});

const safeParticipationPolyline = computed(() => {
	return participationPolyline.value.slice(Math.max(0, safeParticipationPolylineStartIndex.value - 1), Math.min(safeParticipationPolylineEndIndex.value + 2, participationPolyline.value.length - 1));
});
const preSafeParticipationPolyline = computed(() => {
	if (safeParticipationPolylineStartIndex.value > 0) {
		return participationPolyline.value.slice(0, safeParticipationPolylineStartIndex.value);
	}
	return null;
});
const postSafeParticipationPolyline = computed(() => {
	if (safeParticipationPolylineEndIndex.value < participationPolyline.value.length - 1) {
		return participationPolyline.value.slice(safeParticipationPolylineEndIndex.value + 1);
	}
	return null;
});

// We do not want the map bounds to be computed reactive to prevent focus shifts on live updates
// Instead, we only set the focus elements when the user explicitly wants it
const hasEnabledAutoFocus = ref(true);
const mapFocusElements = ref([]);

const selectedParticipationPolylineIndex = ref(0);

const isActive = computed(() => {
	return props.participation.status.label === ParticipationStatus.Active;
});

const hasFinished = computed(() => {
	return !!props.participation.finished_at;
});

const hasBoundingResultTrackSpots = computed(() => {
	if (!mapData.value) {
		return false;
	}

	return mapData.value.trackSpots.some(trackSpot => trackSpot.type === TrackSpotType.Result && trackSpot.lat && trackSpot.lng && (trackSpot.position === 1 || trackSpot.is_finish));
});

watch(mapDataPending, (newValue) => {
	if (!newValue) {
		mapFocusElements.value.push(...mapData.value.trackSpots.filter(trackSpot => trackSpot.lat && trackSpot.lng));
		mapData.value.trackSpotRecords.forEach(record => {
			trackTrackSpotRecord(record);
		});
	}
}, {immediate: true});

watch(participationPolylinePending, (newValue) => {
	if (!newValue) {
		if (participationPolyline.value?.length) {
			mapFocusElements.value.push(participationPolyline.value[0]);

			// Consider round courses by including various intermediate points of the polyline
			for (let i = 1; i < Math.min(11, participationPolyline.value.length); i++) {
				mapFocusElements.value.push(participationPolyline.value[Math.round(participationPolyline.value.length * (i / 10))]);
			}

			mapFocusElements.value.push(participationPolyline.value[participationPolyline.value.length - 1]);

			selectedParticipationPolylineIndex.value = isActive.value ? participationPolyline.value.length - 1 : 0;
		}
	}
}, {immediate: true});

onMounted(() => {
	listenForUpdates();
})

onBeforeUnmount(() => {
	stopListenForUpdates();
})

function getIconForTrackSpot(trackSpot) {
	if (trackSpot.position === 1 && trackSpot.is_finish) {
		return icons.round_result_tracking_spot;
	}
	if (trackSpot.position === 1) {
		return icons.start;
	}
	if (trackSpot.is_finish) {
		return icons.finish;
	}

	return getTrackSpotSubtypeIcon(trackSpot.subtype)
}

function getTrackSpotSubtypeIcon(subtype) {
	switch (subtype) {
		case TrackSpotSubtype.ResultTimeMeasurement:
			return mdiTimerOutline
		case TrackSpotSubtype.ResultTimePause:
			return mdiMotionPauseOutline
		case TrackSpotSubtype.ResultTimeContinue:
			return mdiMotionPlayOutline
	}

	return mdiTimerOutline;
}

function listenForUpdates() {
	if (hasFinished.value) {
		return;
	}

	const participationId = props.participation.id;

	console.log(`Listening for live updates on participation: ${participationId}`);
	$echo.subscribe(`participation.${participationId}`)
		.listen('participation_live_location.refreshed', async () => {
			// When we receive a new location, we refresh the whole polyline to have a smooth animation without gaps as live locations are only tracked every 10th meters
			await refreshPolyline();

			// If the map did not have a focus yet, we set the focus to the current location, even when the user has disabled the auto focus,
			// as otherwise the viewer might not find the participants first location on the map
			if (hasEnabledAutoFocus.value || !mapFocusElements.value.length) {
				selectedParticipationPolylineIndex.value = participationPolyline.value.length - 1;
				mapFocusElements.value = [participationPolyline.value[selectedParticipationPolylineIndex.value]];
			}
		})
		.listen('track_spot_record.created', (record) => {
			trackTrackSpotRecord(record);
		});
}

function stopListenForUpdates() {
	const participationId = props.participation.id;

	console.log(`Stop listening for live updates on participation: ${participationId}`);
	$echo.unsubscribe(`participation.${participationId}`);
}

function trackTrackSpotRecord(record) {
	const trackSpot = mapData.value.trackSpots.find(spot => spot.id === record.track_spot_id);
	if (trackSpot) {
		const existingRecord = trackSpot.records?.find(existingRecord => existingRecord.id === record.id);
		if (!existingRecord) {
			trackSpot['_tracked'] = true;

			if (!trackSpot.records) {
				trackSpot['records'] = [];
			}
			trackSpot.records.push(record);
		}
	}
}

async function downloadTrack() {
	const downloadFileResponse = await $apiFetch(`/v1/events/${props.event.id}/participations/${props.participation.id}/track/download`);

	$analytics.trackEvent('download', {model_type_name: 'participation_track',
		url: downloadFileResponse.data
	});

	$downloadFile(downloadFileResponse.data);
}

function focusParticipant() {
	if (isActive.value) {
		selectedParticipationPolylineIndex.value = participationPolyline.value.length - 1;
		mapFocusElements.value = [participationPolyline.value[selectedParticipationPolylineIndex.value]];
	} else {
		mapFocusElements.value = [];
		if (participationPolyline.value.length) {
			// Consider round courses by including various points of the polyline
			for (let i = 0; i < Math.min(11, participationPolyline.value.length); i++) {
				mapFocusElements.value.push(participationPolyline.value[Math.round(participationPolyline.value.length * (i / 10))]);
			}
		}
	}
	hasEnabledAutoFocus.value = true;
}

function handleTrackSpotClick(trackSpot) {
	selectedTrackSpot.value = trackSpot;
	isTrackSpotDetailsModalVisible.value = true;
}

function refreshAll() {
	refreshPolyline();
	refreshMapData();
}

</script>
